import React, { useState, useEffect, useCallback } from 'react';
import { useGame } from '../../context/GameContext';
import { usePlayer } from '../../context/PlayerContext';
import { useGameRound } from '../../hooks/useGameRound';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import PlayerHand from '../board/PlayerHand';
import OpponentCard from '../board/OpponentCard';
import ScoreBoard from '../ui/ScoreBoard';
import Button from '../ui/Button';
import { getRoundEndTitle, getScoreReaction } from '../../utils/flavorText';

const REVEAL_DURATION = 15;

function RoundEndScreen() {
  const { state } = useGame();
  const { playerId, isHost, resetPlayer } = usePlayer();
  const { dispatch } = useGame();
  const { gameState, players, playerOrder, roundState } = state;
  const { completeRound, endGame } = useGameRound();
  const [countdown, setCountdown] = useState(REVEAL_DURATION);

  const showScores = roundState?.showScores || false;

  useEffect(() => {
    if (showScores) return;
    if (countdown <= 0) {
      if (isHost) advanceToScores();
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, showScores, isHost]);

  const advanceToScores = useCallback(async () => {
    try {
      const fn = httpsCallable(functions, 'showScores');
      await fn({ roomCode: state.roomCode, playerId });
    } catch {}
  }, [state.roomCode, playerId]);

  const handleEndGame = useCallback(async () => {
    try { await endGame(); } catch {}
  }, [endGame]);

  const handleLeave = useCallback(() => {
    resetPlayer();
    dispatch({ type: 'RESET' });
  }, [resetPlayer, dispatch]);

  const opponents = playerOrder.filter((pid) => pid !== playerId);
  const me = players[playerId];
  const myHand = me?.hand ? Object.values(me.hand) : [];

  // Compute context for flavor text
  const myScore = me?.scores?.currentRoundScore ?? 0;
  const allScores = playerOrder.map((pid) => players[pid]?.scores?.currentRoundScore ?? 0);
  const bestScore = Math.min(...allScores);
  const isBest = myScore === bestScore;

  if (!showScores) {
    return (
      <div className="round-end-screen round-end-screen--reveal">
        <div className="game-board__header">
          <span>Round {gameState.currentRound} — All Cards Revealed</span>
          <span className="round-end-screen__timer">{countdown}s</span>
        </div>

        <div className="game-board__opponents">
          {opponents.map((pid) => (
            <OpponentCard key={pid} player={players[pid]} />
          ))}
        </div>

        <PlayerHand hand={myHand} gamePhase="ROUND_END" />

        {isHost && (
          <Button onClick={advanceToScores} variant="secondary" className="round-end-screen__skip">
            Show Scores
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="round-end-screen">
      <h2 className="round-end-screen__title">
        {getRoundEndTitle(gameState.currentRound, myScore, bestScore, isBest)}
      </h2>

      <div className="round-end-screen__results">
        {playerOrder.map((pid) => {
          const player = players[pid];
          if (!player) return null;
          const roundScore = player.scores?.currentRoundScore ?? 0;
          const reaction = getScoreReaction(roundScore);
          return (
            <div key={pid} className="round-end-screen__player">
              <span>{player.displayName}</span>
              <span className="round-end-screen__round-score">
                {roundScore > 0 ? `+${roundScore}` : roundScore}
                <span className="round-end-screen__reaction">{reaction}</span>
              </span>
            </div>
          );
        })}
      </div>

      <ScoreBoard />

      {isHost && (
        <div className="round-end-screen__actions">
          {gameState.currentRound >= gameState.totalRounds ? (
            <Button onClick={completeRound}>Final Scores</Button>
          ) : (
            <>
              <Button onClick={completeRound}>Next Round</Button>
              <Button variant="ghost" onClick={handleEndGame}>End Game</Button>
            </>
          )}
        </div>
      )}

      {!isHost && (
        <div className="round-end-screen__actions">
          <p className="round-end-screen__waiting">The host is lining up the next shot...</p>
          <Button variant="ghost" onClick={handleLeave}>Leave Game</Button>
        </div>
      )}
    </div>
  );
}

export default RoundEndScreen;
